import React from 'react';
import classNames, { Argument } from 'classnames';
import { FormattedMessage } from 'react-intl';

import Label from 'types/Label';
import { ChecklistSeverityEnum } from 'types/Checklist';
import { AbstractMedia, MediaType } from 'types/AbstractMedia';

import useLabelTranslation from 'hooks/useLabelTranslation';

import Carousel from '../Carousel';
import Attachments from '../Attachments';
import Title from './Title';
import InclTaxLabel from '../InclTaxLabel';
import { usePriceFormatter } from 'utils/PriceFormatter/usePriceFormatter';

interface CardProps {
  title: string;
  description?: Label;
  mediaPlaceholder?: AbstractMedia;
  medias?: AbstractMedia[];
  mandatory?: boolean;
  highlighted?: boolean;
  className?: Argument;
  centerText?: boolean;
  severity?: ChecklistSeverityEnum;
  error?: string
}

type CardPriceProps = {
  price?: number;
  discountedPrice?: number;
};

const Card: React.FC<React.PropsWithChildren<CardProps & CardPriceProps>> = ({
  centerText,
  children,
  className,
  description,
  discountedPrice,
  highlighted,
  mandatory,
  mediaPlaceholder,
  medias = [],
  price,
  severity,
  title,
  error
}) => {
  const { getLabelTranslation } = useLabelTranslation();
  const formatPrice = usePriceFormatter();

  const { imagesOrVideos, documents, videos } = React.useMemo(() =>
    medias.reduce((acc, media) => {
      if (media.type === MediaType.IMAGE || media.type === MediaType.VIDEO) {
        acc.imagesOrVideos.push(media);
      }
      if (media.type === MediaType.DOCUMENT) {
        acc.documents.push(media);
      }
      if (media.type === MediaType.VIDEO) {
        acc.videos.push(media);
      }
      return acc;
    }, { imagesOrVideos: [], documents: [], videos: [] }),
  [medias]
  );

  const hasPrice = typeof price === 'number' && price >= 0;
  const hasImagesOrVideos = imagesOrVideos?.length > 0;
  const isOffered = severity === ChecklistSeverityEnum.OFFERED;

  const hasDescription = Boolean(description);
  const hasDiscount = isOffered || (hasPrice && discountedPrice >= 0 && price !== discountedPrice);

  const [videoIndexPreview, setVideoIndexPreview] = React.useState<number>();

  const handleOpenVideoPreview = React.useCallback(
    (media: AbstractMedia) => setVideoIndexPreview(media.type === MediaType.VIDEO ? videos.findIndex((video) => video.id === media.id) : undefined),
    [videos],
  );
  const handleCloseVideoPreview = React.useCallback(() => setVideoIndexPreview(undefined), []);

  return (
    <div className={classNames('rounded-card bg-card-bg overflow-hidden flex flex-col mb-0', className)}>
      {hasImagesOrVideos && <Carousel hideCursor medias={imagesOrVideos} onMediaClick={handleOpenVideoPreview} carouselClassName="carousel-card" />}
      {!hasImagesOrVideos && mediaPlaceholder && <Carousel medias={[mediaPlaceholder]} carouselClassName="carousel-card" />}
      {videoIndexPreview >= 0 && (
        <Carousel
          fullscreen
          medias={videos}
          carouselClassName="carousel-card"
          onClose={handleCloseVideoPreview}
          defaultMediaIndex={videoIndexPreview}
        />
      )}

      <div className="p-xs flex flex-col justify-between grow">
        <div>
          {highlighted && (
            <p data-testid="highlight" className="flex items-center w-fit mb-xs bg-default rounded-3xl text-white px-5 xs:px-4 py-1 text-xs! font-semibold">
              <span className="text-xs! mr-2 text-warning-default">★</span>
              <FormattedMessage
                id="extras.recommended"
                defaultMessage="Recommended for you"
              />
            </p>
          )}
          <div className="mb-xs">
            <div className="font-bold flex">
              <Title title={title} error={error} mandatory={mandatory} centerText={centerText}  />
              {hasPrice && (
                <div data-testid="price" className="flex flex-col text-s! ml-4 items-end">
                  {hasDiscount && (
                    <span className="text-s! ml-0 sm:ml-2 kiosk:leading-10.5!" data-testid="discounted-price">
                      {isOffered && <FormattedMessage id="basket.offered" defaultMessage="Offered" />}
                      {!isOffered && formatPrice(discountedPrice)}
                    </span>
                  )}
                  {hasDiscount && !isOffered && <InclTaxLabel />}
                  <span
                    data-testid="full-price"
                    className={classNames('leading-6 kiosk:leading-10.5!', {
                      'line-through text-default font-normal text-xs!': hasDiscount,
                    })}
                  >
                    {formatPrice(price)}
                  </span>
                  {!hasDiscount && <InclTaxLabel />}
                </div>
              )}
            </div>
          </div>

          {hasDescription && (
            <p
              data-testid="description"
              className={classNames('whitespace-pre-wrap font-normal text-xs! text-low leading-4 mb-xs', { 'text-center': centerText })}
            >
              {getLabelTranslation(description)}
            </p>
          )}
        </div>
        {documents.length > 0 && (
          <div className="mb-xs">
            <div className="text-xs font-bold">
              <FormattedMessage id="media.attachedDocuments" defaultMessage="Attached documents" />
            </div>
            <Attachments
              documents={documents}
              small
              alwaysPreviewable
              showDownloadInPreview
              hideDownloadInList
            />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default Card;
